/**
 * Takım Lideri Performans — ciro Excel
 * 1) «Günlük Asistan Detay» — günlük adet + tutar kolonları
 * 2) «Asistan Randevu Listesi» — satır başına Yapılan Tedavi (İşlem/Muayene/İptal) + Alınan Ücret → gün/asistan toplamı
 * 3) «Asistan Prim Tablosu» — aylık toplam adet + ciro
 */
import * as XLSX from "xlsx";

const cellStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

const normalizeHeader = (s) => cellStr(s)
  .replace(/İ/g, "I")
  .replace(/I/g, "i")
  .replace(/ı/g, "i")
  .toLowerCase()
  .normalize("NFKC")
  .replace(/\u0307/g, "")
  .replace(/\s+/g, " ")
  .replace(/ş/g, "s")
  .replace(/ğ/g, "g")
  .replace(/ü/g, "u")
  .replace(/ö/g, "o")
  .replace(/ç/g, "c");

/** ₺758,350 · 421.000 ₺ · 758350 */
export function parseTlpCiroMoney(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  let s = cellStr(v).replace(/₺/g, "").replace(/\s/g, "");
  if (!s || s === "-") return 0;
  const hasCommaDec = /,\d{1,2}$/.test(s);
  if (hasCommaDec) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",") && !s.includes(".")) {
    s = s.replace(/,/g, "");
  } else {
    s = s.replace(/\./g, "");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** Excel raw satırı boşken formatlı metin/sayıyı kullan (adet kolonları için kritik) */
function coalesceCell(raw, fmt) {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const rs = cellStr(raw);
  if (rs !== "" && rs !== "-") return raw;
  if (typeof fmt === "number" && Number.isFinite(fmt)) return fmt;
  const fs = cellStr(fmt);
  if (fs !== "" && fs !== "-") return fmt;
  return raw ?? fmt;
}

const parseIntCount = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.round(v));
  const s = cellStr(v).replace(/₺/g, "").replace(/\s/g, "");
  if (!s || s === "-") return 0;
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
};

function parseExcelSerialIso(n) {
  if (!Number.isFinite(n) || n < 1000) return null;
  const dc = XLSX.SSF?.parse_date_code?.(n);
  if (dc && dc.y >= 1990 && dc.y <= 2100) {
    return `${dc.y}-${String(dc.m).padStart(2, "0")}-${String(dc.d).padStart(2, "0")}`;
  }
  const ms = Math.round((n - 25569) * 86400000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export function parseTlpCiroTreatmentDate(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    const iso = parseExcelSerialIso(v);
    if (iso) return iso;
  }
  const s = cellStr(v);
  if (!s) return null;
  const dmy = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return null;
}

/** «Asistan Randevu Listesi» — satır başına yapılan tedavi (İşlem/Muayene/İptal) + alınan ücret */
function findRandevuCiroSheet(wb) {
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    const rowsFmt = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
    for (let i = 0; i < Math.min(rowsFmt.length, 20); i++) {
      const line = (rowsFmt[i] || []).map(normalizeHeader);
      const hasYapilan = line.some((c) => c.includes("yapilan tedavi"));
      const hasUcret = line.some((c) => c.includes("alinan ucret"));
      const hasAsistan = line.some((c) => c.includes("asistan bilgi") || c.includes("asistan adi"));
      const hasDate = line.some((c) => c.includes("tedavi tarihi"));
      if (hasYapilan && hasUcret && hasAsistan && hasDate) {
        return { sheetName: name, headerRowIdx: i };
      }
    }
  }
  return null;
}

function mapRandevuHeaderRow(line) {
  const norm = (line || []).map((c) => normalizeHeader(c));
  const idx = (predicates) => {
    for (let i = 0; i < norm.length; i++) {
      const c = norm[i];
      if (!c) continue;
      if (predicates.some((p) => p(c))) return i;
    }
    return -1;
  };
  return {
    date: idx([(c) => c.includes("tedavi tarihi")]),
    assistant: idx([
      (c) => c.includes("asistan bilgi"),
      (c) => c.includes("asistan adi"),
      (c) => c === "asistan",
    ]),
    yapilanTedavi: idx([(c) => c.includes("yapilan tedavi")]),
    ucret: idx([(c) => c.includes("alinan ucret")]),
    leader: idx([(c) => c.includes("takim lideri")]),
  };
}

/** Yapılan Tedavi hücresi → işlem | muayene | iptal (her satır = 1 adet) */
function classifyRandevuTreatment(v) {
  const s = normalizeHeader(v);
  if (!s) return null;
  if (s.includes("iptal")) return "iptal";
  if (s.includes("muayene")) return "muayene";
  if (s.includes("islem") || s.includes("proloterapi")) return "islem";
  return null;
}

function parseRandevuCiroSheet(ws, headerRowIdx = 0) {
  const rowsRaw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  const rowsFmt = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
  const col = mapRandevuHeaderRow(rowsFmt[headerRowIdx] || []);
  if (col.date < 0 || col.assistant < 0 || col.yapilanTedavi < 0 || col.ucret < 0) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Randevu listesi kolonları okunamadı" };
  }

  const buckets = new Map();
  let minDate = null;
  let maxDate = null;

  for (let r = headerRowIdx + 1; r < rowsRaw.length; r++) {
    const rawLine = rowsRaw[r] || [];
    const fmtLine = rowsFmt[r] || [];
    const assistant = cellStr(fmtLine[col.assistant] ?? rawLine[col.assistant]);
    if (!assistant || /^toplam$/i.test(assistant)) continue;

    const dateVal = coalesceCell(rawLine[col.date], fmtLine[col.date]);
    const tedavi = parseTlpCiroTreatmentDate(dateVal)
      ?? parseSlashMDY(fmtLine[col.date])
      ?? parseSlashMDY(rawLine[col.date])
      ?? parseTlpCiroTreatmentDate(fmtLine[col.date]);
    if (!tedavi) continue;

    const kind = classifyRandevuTreatment(coalesceCell(rawLine[col.yapilanTedavi], fmtLine[col.yapilanTedavi]));
    if (!kind) continue;

    const fee = parseTlpCiroMoney(coalesceCell(rawLine[col.ucret], fmtLine[col.ucret]));
    const leader = col.leader >= 0 ? cellStr(fmtLine[col.leader] ?? rawLine[col.leader]) : "";
    const bucketKey = `${tedavi}|${assistant}|${leader}`;

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        tedavi_tarihi: tedavi,
        assistant_name: assistant,
        leader_name: leader,
        work_duration_label: "",
        islem_adeti: 0,
        islem_tutari: 0,
        muayene_adeti: 0,
        muayene_tutari: 0,
        iptal_adeti: 0,
        iptal_tutari: 0,
      });
    }
    const b = buckets.get(bucketKey);
    if (kind === "islem") {
      b.islem_adeti += 1;
      b.islem_tutari += fee;
    } else if (kind === "muayene") {
      b.muayene_adeti += 1;
      b.muayene_tutari += fee;
    } else if (kind === "iptal") {
      b.iptal_adeti += 1;
      b.iptal_tutari += fee;
    }

    if (!minDate || tedavi < minDate) minDate = tedavi;
    if (!maxDate || tedavi > maxDate) maxDate = tedavi;
  }

  const out = [...buckets.values()].map((b) => ({
    ...b,
    toplam_ciro: b.islem_tutari + b.muayene_tutari,
  })).filter((b) => b.islem_adeti + b.muayene_adeti + b.iptal_adeti > 0);

  if (!out.length) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Randevu listesinde okunabilir satır yok" };
  }

  return {
    rows: out,
    periodStart: minDate,
    periodEnd: maxDate,
    format: "assistant_randevu_list",
    error: null,
  };
}

function findDailySheet(wb) {
  const preferred = wb.SheetNames.find((n) => /günlük asistan detay|gunluk asistan detay|asistan tedavi|tedavi.*ciro/i.test(n));
  if (preferred) return preferred;
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
    for (let i = 0; i < Math.min(rows.length, 20); i++) {
      const line = (rows[i] || []).map(normalizeHeader);
      if (line.some((c) => c.includes("yapilan tedavi")) && line.some((c) => c.includes("alinan ucret"))) {
        continue;
      }
      if (line.some((c) => c.includes("tedavi tarihi")) && line.some((c) => c.includes("asistan"))) {
        return name;
      }
    }
  }
  return null;
}

function mapDailyHeaderRow(line) {
  const norm = (line || []).map((c) => normalizeHeader(c));
  const idx = (predicates) => {
    for (let i = 0; i < norm.length; i++) {
      const c = norm[i];
      if (!c) continue;
      if (predicates.some((p) => p(c))) return i;
    }
    return -1;
  };
  const idxLast = (predicates) => {
    let found = -1;
    for (let i = 0; i < norm.length; i++) {
      const c = norm[i];
      if (!c) continue;
      if (predicates.some((p) => p(c))) found = i;
    }
    return found;
  };
  return {
    date: idx([(c) => c.includes("tedavi tarihi")]),
    assistant: idx([
      (c) => c.includes("asistan adi") || c.includes("asistan isim") || c === "asistan",
    ]),
    islemAdet: idxLast([
      (c) => c.includes("islem") && c.includes("adet"),
      (c) => c.includes("islem") && (c.includes("sayi") || c.includes("adedi")),
      (c) => c === "islem adet" || c === "islem adeti",
    ]),
    islemTutar: idxLast([
      (c) => c.includes("islem") && (c.includes("tutar") || c.includes("ciro")),
      (c) => c.includes("islem") && c.includes("toplam"),
    ]),
    muayeneAdet: idxLast([
      (c) => c.includes("muayene") && c.includes("adet"),
      (c) => c.includes("muayene") && (c.includes("sayi") || c.includes("adedi")),
    ]),
    muayeneTutar: idxLast([
      (c) => c.includes("muayene") && (c.includes("tutar") || c.includes("ciro")),
    ]),
    iptalAdet: idxLast([
      (c) => c.includes("iptal") && c.includes("adet"),
      (c) => c.includes("iptal") && (c.includes("sayi") || c.includes("adedi")),
    ]),
    iptalTutar: idxLast([
      (c) => c.includes("iptal") && (c.includes("tutar") || c.includes("ciro")),
    ]),
  };
}

/** Üst satır «İşlem» + alt satır «Adet» birleşimi */
function buildCompositeHeaderMap(parentRow, childRow) {
  const parents = (parentRow || []).map((c) => normalizeHeader(c));
  const children = (childRow || []).map((c) => normalizeHeader(c));
  const len = Math.max(parents.length, children.length);
  const combined = [];
  let carry = "";
  for (let i = 0; i < len; i++) {
    const p = parents[i] || "";
    const ch = children[i] || "";
    if (p) carry = p;
    const merged = [carry, ch].filter(Boolean).join(" ").trim();
    combined.push(merged || ch || carry);
  }
  return mapDailyHeaderRow(combined);
}

/** Tutar kolonunun hemen solundaki küçük tam sayı (yanlış adet kolonu eşleşmesi) */
function intCountBeforeMoneyColumn(rawLine, fmtLine, tutarCol) {
  if (tutarCol == null || tutarCol < 1) return 0;
  for (let c = tutarCol - 1; c >= Math.max(0, tutarCol - 3); c--) {
    const n = parseIntCount(coalesceCell(rawLine[c], fmtLine[c]));
    const asMoney = parseTlpCiroMoney(coalesceCell(rawLine[c], fmtLine[c]));
    if (n > 0 && n <= 999 && asMoney === 0) return n;
  }
  return 0;
}

function resolveDailyColMap(rowsFmt, startIdx) {
  const single = mapDailyHeaderRow(rowsFmt[startIdx] || []);
  if (single.date >= 0 && single.assistant >= 0 && single.islemTutar >= 0) {
    return { colMap: single, dataStart: startIdx + 1 };
  }
  if (startIdx + 1 < rowsFmt.length) {
    const composite = buildCompositeHeaderMap(rowsFmt[startIdx], rowsFmt[startIdx + 1]);
    if (composite.date >= 0 && composite.assistant >= 0 && composite.islemTutar >= 0) {
      return { colMap: composite, dataStart: startIdx + 2 };
    }
  }
  return null;
}

function parseDailyAssistantSheet(ws) {
  const rowsRaw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  const rowsFmt = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });

  let colMap = null;
  let dataStart = -1;
  for (let i = 0; i < Math.min(rowsFmt.length, 30); i++) {
    const resolved = resolveDailyColMap(rowsFmt, i);
    if (resolved) {
      colMap = resolved.colMap;
      dataStart = resolved.dataStart;
      break;
    }
  }
  if (!colMap || dataStart < 0) {
    return { rows: [], periodStart: null, periodEnd: null, error: "«Günlük Asistan Detay» başlık satırı bulunamadı" };
  }

  const out = [];
  let minDate = null;
  let maxDate = null;

  for (let r = dataStart; r < rowsRaw.length; r++) {
    const rawLine = rowsRaw[r] || [];
    const fmtLine = rowsFmt[r] || [];
    const assistant = cellStr(fmtLine[colMap.assistant] ?? rawLine[colMap.assistant]);
    if (!assistant || /^toplam$/i.test(assistant)) continue;

    const dateVal = coalesceCell(rawLine[colMap.date], fmtLine[colMap.date]);
    const tedavi = parseTlpCiroTreatmentDate(dateVal)
      ?? parseTlpCiroTreatmentDate(fmtLine[colMap.date])
      ?? parseTlpCiroTreatmentDate(rawLine[colMap.date]);
    if (!tedavi) continue;

    const islemTutar = parseTlpCiroMoney(coalesceCell(rawLine[colMap.islemTutar], fmtLine[colMap.islemTutar]));
    const muayeneTutar = parseTlpCiroMoney(coalesceCell(rawLine[colMap.muayeneTutar], fmtLine[colMap.muayeneTutar]));
    const iptalTutar = parseTlpCiroMoney(coalesceCell(rawLine[colMap.iptalTutar], fmtLine[colMap.iptalTutar]));

    let islemAdet = colMap.islemAdet >= 0
      ? parseIntCount(coalesceCell(rawLine[colMap.islemAdet], fmtLine[colMap.islemAdet]))
      : 0;
    let muayeneAdet = colMap.muayeneAdet >= 0
      ? parseIntCount(coalesceCell(rawLine[colMap.muayeneAdet], fmtLine[colMap.muayeneAdet]))
      : 0;
    let iptalAdet = colMap.iptalAdet >= 0
      ? parseIntCount(coalesceCell(rawLine[colMap.iptalAdet], fmtLine[colMap.iptalAdet]))
      : 0;

    if (islemAdet === 0 && islemTutar > 0) {
      islemAdet = intCountBeforeMoneyColumn(rawLine, fmtLine, colMap.islemTutar);
    }
    if (muayeneAdet === 0 && muayeneTutar > 0) {
      muayeneAdet = intCountBeforeMoneyColumn(rawLine, fmtLine, colMap.muayeneTutar);
    }
    if (iptalAdet === 0 && iptalTutar > 0) {
      iptalAdet = intCountBeforeMoneyColumn(rawLine, fmtLine, colMap.iptalTutar);
    }

    if (islemAdet === 0 && muayeneAdet === 0 && iptalAdet === 0
      && islemTutar === 0 && muayeneTutar === 0 && iptalTutar === 0) {
      continue;
    }

    const toplamCiro = islemTutar + muayeneTutar;
    out.push({
      tedavi_tarihi: tedavi,
      assistant_name: assistant,
      leader_name: "",
      work_duration_label: "",
      islem_adeti: islemAdet,
      islem_tutari: islemTutar,
      muayene_adeti: muayeneAdet,
      muayene_tutari: muayeneTutar,
      iptal_adeti: iptalAdet,
      iptal_tutari: iptalTutar,
      toplam_ciro: toplamCiro,
    });

    if (!minDate || tedavi < minDate) minDate = tedavi;
    if (!maxDate || tedavi > maxDate) maxDate = tedavi;
  }

  if (!out.length) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Okunabilir günlük satır yok" };
  }

  return {
    rows: out,
    periodStart: minDate,
    periodEnd: maxDate,
    format: "daily_assistant_detail",
    error: null,
  };
}

const TR_MONTHS = {
  ocak: 1, subat: 2, şubat: 2, mart: 3, nisan: 4, mayis: 5, mayıs: 5,
  haziran: 6, temmuz: 7, agustos: 8, ağustos: 8, eylul: 9, eylül: 9,
  ekim: 10, kasim: 11, kasım: 11, aralik: 12, aralık: 12,
};

function parseSlashMDY(v) {
  const s = cellStr(v);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  let y = parseInt(m[3], 10);
  if (m[3].length === 2) y += y < 50 ? 2000 : 1900;
  return isoFromParts(y, parseInt(m[2], 10), parseInt(m[1], 10));
}

function isoFromParts(y, m, d) {
  if (y < 1990 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function parseTrPeriodRange(text) {
  const s = cellStr(text).toLowerCase();
  const m = s.match(
    /(\d{1,2})\s+([a-zçğıöşü]+)\s*-\s*(\d{1,2})\s+([a-zçğıöşü]+)\s+(\d{4})/i,
  );
  if (!m) return { start: null, end: null };
  const m1 = TR_MONTHS[m[2].replace(/ı/g, "i")] ?? null;
  const m2 = TR_MONTHS[m[4].replace(/ı/g, "i")] ?? null;
  if (!m1 || !m2) return { start: null, end: null };
  return {
    start: isoFromParts(parseInt(m[5], 10), m1, parseInt(m[1], 10)),
    end: isoFromParts(parseInt(m[5], 10), m2, parseInt(m[3], 10)),
  };
}

function findPrimSheet(wb) {
  return wb.SheetNames.find((n) => /prim tablosu|asistan prim/i.test(n)) || null;
}

function scanRowDatesForPeriod(rowsFmt) {
  let min = null;
  let max = null;
  for (let i = 0; i < Math.min(rowsFmt.length, 4); i++) {
    for (const cell of rowsFmt[i] || []) {
      const tr = parseTrPeriodRange(cellStr(cell));
      if (tr.start && tr.end) {
        return { periodStart: tr.start, periodEnd: tr.end };
      }
      if (tr.start) min = tr.start;
      if (tr.end) max = tr.end;
    }
  }
  if (min && max) return { periodStart: min, periodEnd: max };
  const slashDates = [];
  for (let i = 0; i < Math.min(rowsFmt.length, 4); i++) {
    for (const cell of rowsFmt[i] || []) {
      const iso = parseSlashMDY(cell);
      if (iso) slashDates.push(iso);
    }
  }
  if (slashDates.length) {
    slashDates.sort();
    return { periodStart: slashDates[0], periodEnd: slashDates[slashDates.length - 1] };
  }
  return { periodStart: min, periodEnd: max };
}

/** «Asistan Prim Tablosu» — aylık toplam adet + ciro (dönem sonu tarihli tek satır / asistan) */
function parsePrimAssistantSheet(ws) {
  const rowsRaw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  const rowsFmt = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });

  let headerRowIdx = -1;
  let norm = [];
  for (let i = 0; i < Math.min(rowsFmt.length, 8); i++) {
    const line = rowsFmt[i] || [];
    const n = line.map(normalizeHeader);
    if (n.some((c) => c.includes("asistan isim")) && n.some((c) => c.includes("aylik toplam islem adet"))) {
      headerRowIdx = i;
      norm = n;
      break;
    }
  }
  if (headerRowIdx < 0) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Prim tablosu başlığı bulunamadı" };
  }

  const idx = (pred) => {
    for (let i = 0; i < norm.length; i++) {
      if (pred(norm[i])) return i;
    }
    return -1;
  };
  const col = {
    leader: idx((c) => c.includes("takim lideri")),
    assistant: idx((c) => c.includes("asistan isim") || c.includes("asistan adi")),
    work: idx((c) => c.includes("calisma") && (c.includes("gun") || c.includes("yil"))),
    islemAdet: idx((c) => c.includes("aylik toplam islem adet")),
    ciro: idx((c) => c.includes("aylik toplam asistan ciro") || c.includes("aylik toplam ciro")),
    muayeneAdet: idx((c) => c.includes("aylik toplam muayene adet")),
    iptalAdet: idx((c) => c.includes("aylik toplam iptal adet")),
  };
  if (col.assistant < 0 || col.ciro < 0) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Prim tablosu kolonları okunamadı" };
  }

  const { periodStart, periodEnd } = scanRowDatesForPeriod(rowsFmt);
  const tedavi = periodEnd || periodStart;
  if (!tedavi) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Prim tablosu dönem tarihi bulunamadı" };
  }

  const out = [];
  for (let r = headerRowIdx + 1; r < rowsRaw.length; r++) {
    const rawLine = rowsRaw[r] || [];
    const fmtLine = rowsFmt[r] || [];
    const assistant = cellStr(fmtLine[col.assistant] ?? rawLine[col.assistant]);
    if (!assistant || /^toplam$/i.test(assistant) || /^\d+$/.test(assistant) && !fmtLine[col.assistant]) continue;
    if (!assistant || assistant.length < 3) continue;

    const leader = col.leader >= 0 ? cellStr(fmtLine[col.leader] ?? rawLine[col.leader]) : "";
    const work = col.work >= 0 ? cellStr(fmtLine[col.work] ?? rawLine[col.work]) : "";
    const islemAdet = col.islemAdet >= 0
      ? parseIntCount(coalesceCell(rawLine[col.islemAdet], fmtLine[col.islemAdet]))
      : 0;
    const muayeneAdet = col.muayeneAdet >= 0
      ? parseIntCount(coalesceCell(rawLine[col.muayeneAdet], fmtLine[col.muayeneAdet]))
      : 0;
    const iptalAdet = col.iptalAdet >= 0
      ? parseIntCount(coalesceCell(rawLine[col.iptalAdet], fmtLine[col.iptalAdet]))
      : 0;
    const toplamCiro = parseTlpCiroMoney(coalesceCell(rawLine[col.ciro], fmtLine[col.ciro]));

    if (islemAdet === 0 && muayeneAdet === 0 && iptalAdet === 0 && toplamCiro === 0) continue;

    out.push({
      tedavi_tarihi: tedavi,
      assistant_name: assistant,
      leader_name: leader,
      work_duration_label: work,
      islem_adeti: islemAdet,
      islem_tutari: 0,
      muayene_adeti: muayeneAdet,
      muayene_tutari: 0,
      iptal_adeti: iptalAdet,
      iptal_tutari: 0,
      toplam_ciro: toplamCiro,
    });
  }

  if (!out.length) {
    return { rows: [], periodStart: null, periodEnd: null, error: "Prim tablosunda okunabilir satır yok" };
  }

  return {
    rows: out,
    periodStart: periodStart || tedavi,
    periodEnd: periodEnd || tedavi,
    format: "assistant_prim_table",
    error: null,
  };
}

export function parseTlpCiroWorkbook(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array", cellDates: false });

  const randevu = findRandevuCiroSheet(wb);
  if (randevu) {
    const result = parseRandevuCiroSheet(wb.Sheets[randevu.sheetName], randevu.headerRowIdx);
    if (result.rows?.length) {
      return { ...result, sheetName: randevu.sheetName };
    }
  }

  const dailySheet = findDailySheet(wb);
  if (dailySheet) {
    const result = parseDailyAssistantSheet(wb.Sheets[dailySheet]);
    if (result.rows?.length) {
      return { ...result, sheetName: dailySheet };
    }
  }
  const primSheet = findPrimSheet(wb);
  if (primSheet) {
    const result = parsePrimAssistantSheet(wb.Sheets[primSheet]);
    return { ...result, sheetName: primSheet };
  }
  return {
    rows: [],
    periodStart: null,
    periodEnd: null,
    error: "Desteklenen format bulunamadı: «Günlük Asistan Detay», «Asistan Randevu Listesi» (yapılan tedavi + ücret) veya «Asistan Prim Tablosu».",
  };
}
